import { WordlistConfiguration, type WorksheetSize } from "@arbeidsark/shared"
import range from "lodash/range"
import { useMemo, useState } from "react"
import styled from "styled-components"
import { Button, Input } from "~/components"
import { Icon, Modal } from "~/ur-react-components"
import { useTranslate } from "~/ur-react-hooks"

const Layout = styled.div`
  background: white;
  border-radius: 0.5rem;

  width: 328px;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;

  padding: 1rem;
`

const Title = styled.h4`
  margin: 0;
  font-weight: 500;
  color: ${(props) => props.theme.colors.gray0};
  text-transform: uppercase;
`

const HeaderCloseButton = styled.button`
  outline: none;
  border: none;

  cursor: pointer;
  background: ${(props) => props.theme.colors.gray80};
  color: white;
  width: 24px;
  height: 24px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${(props) => props.theme.colors.gray70};
  }
`

const SectionLabel = styled.label`
  display: inline-block;
  margin-top: 1.5rem;
  margin-bottom: 8px;
  color: ${(props) => props.theme.colors.gray0};
`

const Body = styled.div`
  max-height: 550px;
  overflow-y: auto;

  padding: 1rem;
  padding-top: 0;
`

const InputsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const SaveButtonWrapper = styled.div`
  margin-top: 1.5rem;
  padding: 1rem;
`

export interface WordlistModalProps {
  isOpen: boolean
  onClose: (newConfiguration?: WordlistConfiguration) => void

  layout: WorksheetSize
  configuration?: unknown

  pageIndex: number
  moduleIndex: number
}

const WordlistModal: React.FC<WordlistModalProps> = ({
  isOpen,
  onClose,
  pageIndex,
  moduleIndex,
  layout,
  configuration: _configuration,
}) => {
  const translations = useTranslate({
    page: "worksheets.labels.page",
    task: "worksheets.labels.task",
    configure: "worksheets.labels.configure",
    editWords: "worksheets.wordlist-modal.edit-words",
    save: "common.save",
  })

  const wordCount = layout === "full" ? 12 : 6

  const parsedConfiguration = useMemo(
    () => WordlistConfiguration.safeParse(_configuration),
    [_configuration],
  )

  const [configuration, setConfiguration] = useState<WordlistConfiguration>(
    parsedConfiguration.success
      ? parsedConfiguration.data
      : {
          wordlist: range(12).map(() => ""),
        },
  )

  return (
    <Modal open={isOpen} onClose={() => onClose()}>
      <Layout>
        <Header>
          <Title>
            {translations.page} {pageIndex + 1} / {translations.task}{" "}
            {moduleIndex + 1} / {translations.configure}
          </Title>
          <HeaderCloseButton onClick={() => onClose()}>
            <Icon icon="close" size="14px" type="solid" />
          </HeaderCloseButton>
        </Header>
        <Body>
          <SectionLabel>{translations.editWords}</SectionLabel>
          <InputsWrapper>
            {range(wordCount).map((i) => (
              <Input
                value={configuration.wordlist[i] ?? ""}
                height="48px"
                onChange={(value) => {
                  const newWordlist = [...configuration.wordlist]
                  newWordlist[i] = value

                  setConfiguration({
                    ...configuration,
                    wordlist: newWordlist,
                  })
                }}
              />
            ))}
          </InputsWrapper>
        </Body>
        <SaveButtonWrapper>
          <Button
            width="100%"
            type="button"
            onClick={() => onClose(configuration)}
          >
            {translations.save}
          </Button>
        </SaveButtonWrapper>
      </Layout>
    </Modal>
  )
}

export default WordlistModal
